import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';

export type EventqMediaThumbVariant = 'snapshot' | 'clip';

/** True when the URL looks like a video file rather than a still image. */
export function isVideoMediaUrl(url: string | null | undefined): boolean {
  return /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url?.trim() ?? '');
}

/**
 * Compact media card (Figma “Image Darkmode”) for incident evidence strips.
 */
@Component({
  selector: 'eventq-media-thumb-card',
  imports: [MatIcon],
  templateUrl: './media-thumb-card.html',
  styleUrl: './media-thumb-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'eventq-media-thumb-card',
    '[attr.data-variant]': 'variant()',
  },
})
export class EventqMediaThumbCard {
  readonly label = input.required<string>();
  readonly imageSrc = input.required<string>();
  /** When set on clip tiles, the thumb plays muted video instead of a static poster. */
  readonly videoSrc = input<string | undefined>(undefined);
  readonly variant = input<EventqMediaThumbVariant>('snapshot');
  readonly deletable = input(true);

  /** Clip tile with a video poster and no looping `videoSrc` — paused first frame. */
  readonly showPausedVideoStill = computed(
    () => this.variant() === 'clip' && !this.videoSrc() && isVideoMediaUrl(this.imageSrc()),
  );

  /** Hide the video element until the first frame is decoded so polls/loads do not flash black. */
  readonly videoFrameReady = linkedSignal({
    source: () => `${this.imageSrc()}|${this.videoSrc() ?? ''}|${this.variant()}`,
    computation: () => false,
  });

  readonly open = output<void>();
  readonly delete = output<void>();

  markVideoFrameReady(): void {
    this.videoFrameReady.set(true);
  }

  onOpen(): void {
    this.open.emit();
  }

  onDelete(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.delete.emit();
  }
}
